'use client';

import { c as _c } from "react/compiler-runtime";
import type { SearchFilters } from '@/features/payload-cms/components/search/search-filter-panel';
import { parseSearchWords } from '@/features/payload-cms/search/search-text';
import type { SiteSearchResponse } from '@/features/payload-cms/search/search-types';
import { buildSearchQueryString } from '@/features/payload-cms/search/search-types';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { trpc } from '@/trpc/client';
import type { Locale } from '@/types/types';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

/** Short enough to feel instant, long enough not to query for every key typed. */
export const QUERY_DELAY_MS = 200;
/** One letter matches nearly everything; it only makes the list jump. */
export const MIN_QUERY_LENGTH = 2;
const RESULTS_PER_PAGE = 20;

/**
 * What the search shows. `loading` covers the time between a keystroke and its answer, so a
 * changed query never shows the results of the one before it.
 */
export type LiveSearchStatus = 'idle' | 'loading' | 'offline' | 'error' | 'ready';
export interface LiveSearch {
  draft: string;
  setDraft: (draft: string) => void;
  filters: SearchFilters;
  setFilters: (change: Partial<SearchFilters>) => void;
  page: number;
  setPage: (page: number) => void;
  status: LiveSearchStatus;
  /** the answer for exactly the query and filters on screen, undefined while it is on its way */
  response: SiteSearchResponse | undefined;
  /** the words of the query the response answers, for highlighting */
  words: string[];
  retry: () => void;
}

/** Keeps the query string in step without a server round trip, so a search stays shareable. */
const useSearchInUrl = queryString => {
  const $ = _c(4);
  const pathname = usePathname();
  let t0;
  let t1;
  if ($[0] !== pathname || $[1] !== queryString) {
    t0 = () => {
      if (globalThis.location.search === `?${queryString}`) {
        return;
      }
      globalThis.history.replaceState(undefined, "", `${pathname}?${queryString}`);
    };
    t1 = [pathname, queryString];
    $[0] = pathname;
    $[1] = queryString;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};

/**
 * The search page's query, filters and results, answered while the visitor types. The server
 * renders the first answer; every later one comes from the search query of the API, which the
 * instant search in the header uses too, so a query searched there is already cached here.
 */
export const useLiveSearch = (locale, initial) => {
  const $ = _c(32);
  const isOnline = useOnlineStatus();
  const [draft, setDraft] = useState(initial.query);
  const [filters, setFilters] = useState(initial.filters);
  const [page, setPage] = useState(initial.page);
  let t0;
  if ($[0] !== draft) {
    t0 = draft.trim();
    $[0] = draft;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const trimmed = t0;
  const query = useDebouncedValue(trimmed, QUERY_DELAY_MS);
  const isTyping = trimmed !== query;
  const isSearchable = query.length >= MIN_QUERY_LENGTH;
  const isInitial = query === initial.query && page === initial.page && filters.sort === initial.filters.sort && filters.period === initial.filters.period && filters.types.join(",") === initial.filters.types.join(",");
  let t1;
  if ($[2] !== filters || $[3] !== locale || $[4] !== page || $[5] !== query) {
    t1 = {
      locale,
      query,
      ...filters,
      page,
      pageSize: RESULTS_PER_PAGE
    };
    $[2] = filters;
    $[3] = locale;
    $[4] = page;
    $[5] = query;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = isSearchable && isOnline;
  let t3;
  if ($[7] !== initial.response || $[8] !== isInitial) {
    t3 = isInitial ? {
      initialData: initial.response
    } : {};
    $[7] = initial.response;
    $[8] = isInitial;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      persist: false
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3) {
    t5 = {
      enabled: t2,
      ...t3,
      staleTime: 60000,
      meta: t4
    };
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const result = trpc.search.getSearchResultList.useQuery(t1, t5);
  let t6;
  if ($[14] !== filters || $[15] !== page || $[16] !== trimmed) {
    t6 = buildSearchQueryString({
      query: trimmed,
      ...filters,
      page
    });
    $[14] = filters;
    $[15] = page;
    $[16] = trimmed;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  useSearchInUrl(t6);
  const response = isTyping || !isSearchable ? undefined : result.data;
  let status = "loading";
  if (trimmed.length < MIN_QUERY_LENGTH) {
    status = "idle";
  } else {
    if (response !== undefined) {
      status = "ready";
    } else {
      if (!isOnline) {
        status = "offline";
      } else {
        if (!isTyping && result.isError) {
          status = "error";
        }
      }
    }
  }
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = next => {
      setDraft(next);
      setPage(1);
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = change => {
      setFilters(current => ({
        ...current,
        ...change
      }));
      setPage(1);
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = status;
  let t10;
  if ($[20] !== query) {
    t10 = parseSearchWords(query);
    $[20] = query;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== result) {
    t11 = () => {
      result.refetch();
    };
    $[22] = result;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== draft || $[25] !== filters || $[26] !== page || $[27] !== response || $[28] !== status || $[29] !== t10 || $[30] !== t11) {
    t12 = {
      draft,
      setDraft: t7,
      filters,
      setFilters: t8,
      page,
      setPage,
      status: t9,
      response,
      words: t10,
      retry: t11
    };
    $[24] = draft;
    $[25] = filters;
    $[26] = page;
    $[27] = response;
    $[28] = status;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};